import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { mangaList } from "@/mocks/manga";

const navItems = [
  { label: "首页", to: "/" },
  { label: "排行榜", to: "/rankings" },
  { label: "新作推荐", to: "/new" },
  { label: "完结作品", to: "/completed" },
  { label: "免费专区", to: "/free" },
  { label: "关于我们", to: "/about" },
  { label: "常见问题", to: "/faq" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setKeyword("");
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = keyword.trim()
    ? mangaList
        .filter((m) =>
          [m.title, m.author, ...m.tags].some((t) =>
            t.toLowerCase().includes(keyword.trim().toLowerCase())
          )
        )
        .slice(0, 6)
    : [];

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!keyword.trim()) return;
    navigate(`/rankings?q=${encodeURIComponent(keyword.trim())}`);
    setSearchOpen(false);
    setKeyword("");
  }

  function isActive(to: string) {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  }

  return (
    <header className="sticky top-0 z-50 border-b border-background-200/70 bg-background-50/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        {/* logo */}
        <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="歪歪漫画网 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-lg text-background-50">
            <i className="ri-book-open-line" />
          </span>
          <span className="font-heading text-xl leading-none text-foreground-950 md:text-2xl">
            歪歪<span className="text-primary-600">漫画</span>网
          </span>
        </Link>

        {/* desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navItems.slice(0, 2).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:bg-background-200/70 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}

          {/* 分类下拉 */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                location.pathname.startsWith("/category")
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:bg-background-200/70 hover:text-foreground-950"
              }`}
              aria-haspopup="true"
              aria-expanded={catOpen}
            >
              分类大全 <i className={`ri-arrow-down-s-line text-base ${catOpen ? "rotate-180" : ""}`} />
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full mt-2 w-64 rounded-xl border border-background-200/70 bg-background-50 p-2 shadow-sm">
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((c) => (
                    <Link
                      key={c.id}
                      to={`/category/${c.id}`}
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      <i className={`${c.icon} text-primary-500`} />
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {navItems.slice(2).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:bg-background-200/70 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* search + hamburger */}
        <div className="flex items-center gap-2">
          <div ref={searchRef} className="relative">
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-background-200/80 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              aria-label="搜索漫画"
            >
              <i className="ri-search-line text-lg" />
            </button>
            {searchOpen && (
              <form
                onSubmit={submitSearch}
                className="absolute right-0 top-full mt-2 w-72 rounded-xl border border-background-200/80 bg-background-50 p-2 shadow-sm sm:w-80"
              >
                <div className="flex items-center gap-2 rounded-lg bg-background-100 px-3">
                  <i className="ri-search-line text-foreground-500" />
                  <input
                    autoFocus
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索漫画、标签或作者"
                    className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                  />
                  <button type="submit" className="whitespace-nowrap text-sm font-medium text-primary-600">
                    搜索
                  </button>
                </div>
                {results.length > 0 && (
                  <ul className="mt-1 max-h-72 overflow-auto">
                    {results.map((m) => (
                      <li key={m.id}>
                        <Link
                          to={`/manga/${m.id}`}
                          className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-background-100"
                        >
                          <img src={m.cover} alt={m.title} className="h-12 w-9 rounded-md object-cover" />
                          <span className="flex-1">
                            <span className="block truncate text-sm font-medium text-foreground-950">{m.title}</span>
                            <span className="block text-xs text-foreground-500">
                              {m.category} · {m.status}
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                {keyword.trim() && results.length === 0 && (
                  <p className="px-2 py-3 text-center text-sm text-foreground-500">未找到相关漫画</p>
                )}
              </form>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-200/80 text-foreground-700 lg:hidden"
            aria-label="展开菜单"
            aria-expanded={mobileOpen}
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-lg`} />
          </button>
        </div>
      </div>

      {/* mobile menu */}
      {mobileOpen && (
        <div className="border-t border-background-200/70 bg-background-50 lg:hidden">
          <nav className="mx-auto max-w-7xl px-4 py-3 md:px-6" aria-label="移动端导航">
            <div className="flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`rounded-lg px-3 py-2.5 text-sm font-medium ${
                    isActive(item.to)
                      ? "bg-primary-50 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div className="mt-2 border-t border-background-200/70 pt-2">
              <p className="px-3 pb-1 text-xs text-foreground-500">全部分类</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/category/${c.id}`}
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-background-100"
                  >
                    <i className={`${c.icon} text-primary-500`} />
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}